import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";
import {
  Elements,
  useStripe,
  useElements,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useAuth0 } from "react-auth0-spa";
import { usePostHog } from "@posthog/react";
import { useTranslation } from "react-i18next";
import ReCAPTCHA from "react-google-recaptcha";
import CheckoutV2Api from "Api/CheckoutV2Api";
import { getDonationPlanCode } from "Utils/Components/Plan";
import { DONATE_PAGE_VERSION_KEY } from "Utils/Constants";
import DonationForm, { DonationFormState } from "./DonationForm";
import DonationSummaryV2 from "./DonationSummaryV2";
import MemberGiftSlider from "./MemberGiftSlider";
import { useAchPlaid } from "./useAchPlaid";
import {
  additionalCents,
  confirmPaymentIntent,
  frequencyToDonationType,
  toCents,
} from "./checkoutV2Payment";
import {
  calcCoverFee,
  DonorVariant,
  FONT_SANS,
  FORM_BG,
  GIFT_MINIMUM,
  MIN_DONATION_AMOUNT,
  TRIAL_DAYS,
  getAmountsForFrequency,
  getDefaultAmount,
  getMembershipEndDate,
  isMembershipInactive,
  ONE_TIME_DEFAULT_AMOUNT,
  SECTION_BG,
  shouldShowAutoRenew,
} from "./checkoutV2.constants";

/**
 * Remembers new-donor donations (amount + type) by email in localStorage so that
 * if the donor pays now but only creates their account later (the
 * "complete-registration" path), the account_created_post_donation event can
 * still report the amount/type. Same-browser only — a cross-device return can't
 * recover it (the /user endpoint doesn't return the paid amount).
 */
const PAID_DONATIONS_KEY = "jg_paid_donations";

const rememberPaidDonation = (
  email: string,
  amount: number,
  donationType: string
) => {
  try {
    const map = JSON.parse(localStorage.getItem(PAID_DONATIONS_KEY) || "{}");
    map[email.trim().toLowerCase()] = { amount, donationType };
    localStorage.setItem(PAID_DONATIONS_KEY, JSON.stringify(map));
  } catch {}
};

const readPaidDonation = (
  email: string
): { amount?: number; donationType?: string } | null => {
  try {
    const map = JSON.parse(localStorage.getItem(PAID_DONATIONS_KEY) || "{}");
    return map[email.trim().toLowerCase()] || null;
  } catch {
    return null;
  }
};

const INITIAL_STATE: DonationFormState = {
  email: "",
  frequency: "one-time",
  amount: ONE_TIME_DEFAULT_AMOUNT,
  otherAmount: "",
  paymentMethod: "card",
  giftToSomeone: false,
  coverFees: false,
  // Auto-renew is opt-out: checked by default whenever the option is visible.
  autoRenew: true,
  zip: "",
};

/** Fixed trial price, in dollars, matching FREE_TRIAL_AMOUNT in the lambda. */
const FREE_TRIAL_DOLLARS = 10;

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY as string);

interface DonateSectionProps {
  /**
   * V3 layout: render the "Your Information" block below the "Choose Donation
   * Amount" block within the form. Defaults to the V2 order (info on top).
   */
  infoBelowAmount?: boolean;
}

/**
 * Second section of the Donate page redesign: the donation form (left) and the
 * live summary + member-gift carousel (right). State is owned here so the
 * summary reflects the donor's selections in real time.
 */
const DonateSectionInner = ({ infoBelowAmount }: DonateSectionProps) => {
  const history = useHistory();

  // Free trial: /checkout/v2?coupon=… (or v3). Any coupon starts a trial — the
  // value isn't matched against a fixed code, because campaign coupons vary and
  // most won't exist in Chargebee. This mirrors the checkout lambda, which uses
  // the same rule and then decides separately whether the discount can apply;
  // matching on one literal code here would show the ordinary donate form while
  // the lambda quietly created a trial. Read once — the URL doesn't change under
  // the page — and it only drives the UI: the lambda fixes plan, price and term.
  // Which donate page this is, for Chargebee's cf_Donate_Page_Version. V3 is this
  // same form with the info block moved, and both post to the one checkout
  // endpoint, so only the path tells them apart.
  const donatePageVersion = history.location.pathname.startsWith("/checkout/v3")
    ? "V3"
    : "V2";

  const coupon = new URLSearchParams(history.location.search).get("coupon") || "";
  const trialMode = !!coupon.trim();

  // A trial is a fixed $10/month membership, so the amount and frequency are
  // pinned from the start rather than chosen (the amount block is hidden).
  const [state, setState] = useState<DonationFormState>(() =>
    trialMode
      ? {
          ...INITIAL_STATE,
          frequency: "monthly",
          amount: FREE_TRIAL_DOLLARS,
          autoRenew: true,
        }
      : INITIAL_STATE
  );
  const { isAuthenticated, user, loginWithEmail, applyPlanData } = useAuth0();
  const posthog = usePostHog();
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  // Plaid (ACH / "Checking Account") integration for the direct_debit option.
  const plaid = useAchPlaid();
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  // Analytics: mirror the legacy checkout funnel. Fired once when the donate
  // page mounts (matches the old flow's donation.flow_started on entry).
  useEffect(() => {
    posthog?.capture("donation.flow_started");
  }, [posthog]);

  const [signingIn, setSigningIn] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Signed-out recognition: "email" (new visitor) / "sign-in" (has a login) /
  // "complete-registration" (paid before but never created a login).
  const [authStep, setAuthStep] = useState<
    "email" | "sign-in" | "complete-registration"
  >("email");
  const [checkingEmail, setCheckingEmail] = useState(false);
  // Donation type ("one-time"|"monthly") of a recognized paid-but-unregistered
  // donor's past donation, derived from their plan — reported on the Thank You
  // account_created_post_donation event.
  const [recognizedDonationType, setRecognizedDonationType] = useState<
    string | undefined
  >();
  // Paid amount (cents) from /user?amount=true for a recognized paid-but-
  // unregistered donor — used when their donation was on another device (so the
  // localStorage record isn't available).
  const [recognizedAmount, setRecognizedAmount] = useState<number | undefined>();

  // Stripe card-field completeness + submit state.
  const [cardComplete, setCardComplete] = useState({
    number: false,
    expiry: false,
    cvc: false,
  });
  const cardReady = cardComplete.number && cardComplete.expiry && cardComplete.cvc;
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Pre-fill the email for a signed-in donor (without clobbering a manual edit).
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      setState((prev) => (prev.email ? prev : { ...prev, email: user.email as string }));
    }
  }, [isAuthenticated, user]);

  // Set the moment a donation completes so the active-redirect guard below
  // doesn't fire when we flip the member to active (applyPlanData) and instead
  // let the Thank You navigation win. Scoped to this page visit (a fresh arrival
  // remounts with a clean ref, so an already-active member still redirects).
  const justDonatedRef = useRef(false);

  // Step 3a: an active member doesn't belong on the donate redesign — send them
  // to the existing "Active Membership" page. This covers every entry path
  // (form sign-in, header login, or arriving already signed in). Inactive
  // members stay for the Version 1 donate-to-restore experience. Skipped right
  // after a restore donation so the donor reaches the Thank You modal, not here.
  useEffect(() => {
    if (
      isAuthenticated &&
      !isMembershipInactive(user) &&
      !justDonatedRef.current
    ) {
      history.push("/checkout/one-time");
    }
  }, [isAuthenticated, user, history]);

  // Choose the top-of-form block:
  //  - signed in                     → "member" greeting (active / inactive)
  //  - recognized existing account   → "sign-in" (Version 2)
  //  - otherwise                     → "new" visitor
  const variant: DonorVariant = isAuthenticated
    ? "member"
    : authStep === "sign-in"
    ? "sign-in"
    : authStep === "complete-registration"
    ? "complete-registration"
    : "new";

  const membershipInactive = isAuthenticated && isMembershipInactive(user);
  const membershipEndDate = isAuthenticated ? getMembershipEndDate(user) : null;

  const isValidEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  // Recognize an email against BOTH the Auth0 login and the Dynamo record. Per
  // the Version 2 spec, ANY existing account (active OR inactive) must sign in
  // first (Step 2). Membership routing happens after login (Step 3, via the page
  // guard above: active → Active Membership page, inactive → Version 1).
  //  - "sign-in"                → has a login → open the Auth0 popup (Step 2)
  //  - "complete-registration"  → paid before (Dynamo) but no login yet
  //  - "new"                    → neither
  const recognize = async (
    email: string
  ): Promise<{
    status: "sign-in" | "complete-registration" | "new";
    plan: string;
    amount?: number;
  }> => {
    const { exists: dynamoExists, plan, amount } =
      await CheckoutV2Api.getAccountStatus(email);
    const auth0Exists = await CheckoutV2Api.checkAuth0Login(email);
    // Fall back to the Dynamo check if the Auth0 check endpoint isn't deployed.
    const hasLogin = auth0Exists === null ? dynamoExists : auth0Exists;
    const status = hasLogin
      ? "sign-in"
      : dynamoExists
      ? "complete-registration"
      : "new";
    return { status, plan, amount };
  };

  /** monthly plan codes (e.g. "premium-plus-monthly") → "monthly"; else one-time. */
  const donationTypeFromPlan = (plan: string) =>
    plan.includes("monthly") ? "monthly" : "one-time";

  // On email blur, route by recognition.
  const handleEmailBlur = async () => {
    if (isAuthenticated) return;
    const email = state.email.trim();
    if (!isValidEmail(email)) return;
    setCheckingEmail(true);
    setAuthError(null);
    try {
      const { status, plan, amount } = await recognize(email);
      if (status === "sign-in") {
        // Existing account → force the Auth0 login popup immediately (no
        // intermediate "Welcome back" screen). Step 3 (active → Active Membership
        // page; inactive → Version 1) runs after login via the page guard.
        // NOTE: opening a popup after this async recognition may be blocked by
        // the browser's popup blocker (the blur gesture is already consumed).
        await handleSignIn();
      } else if (status === "complete-registration") {
        // Remember the past donation's type (from the plan) + amount (from
        // /user?amount=true) so the Thank You step can report them on
        // account_created_post_donation.
        setRecognizedDonationType(donationTypeFromPlan(plan));
        setRecognizedAmount(amount);
        setAuthStep("complete-registration");
      } else {
        setAuthStep("email");
      }
    } catch {
      setAuthStep("email");
    } finally {
      setCheckingEmail(false);
    }
  };

  // Paid-but-not-registered donor finishes setup → open the Thank You modal's
  // account-creation form (name + password → CompleteRegistration). No charge.
  // Amount/type come from the localStorage record of their earlier payment (same
  // browser); the plan-derived type is the fallback for a cross-device return.
  const handleCompleteRegistration = () => {
    const email = state.email.trim();
    const stored = readPaidDonation(email);
    // Amount: the backend (/user?amount=true) is authoritative and works
    // cross-device; the localStorage copy is the same-browser fallback. Type:
    // plan-derived from recognition, else the stored value.
    history.push("/", {
      openThankYou: true,
      isNewDonor: true,
      email,
      amount: recognizedAmount ?? stored?.amount,
      donationType: recognizedDonationType ?? stored?.donationType,
    });
  };

  // "Not you? Use a different email" — back to the email-entry block.
  const handleUseDifferentEmail = () => {
    setAuthError(null);
    setAuthStep("email");
    setState((prev) => ({ ...prev, email: "" }));
  };

  // "Already have one? Sign in": open the Auth0 popup with the entered email
  // pre-filled. Membership routing (active → /checkout/one-time; inactive →
  // stay for the Version 1 donate-to-restore experience) is handled by the
  // page-level guard above once the user resolves. Triggered by the link click
  // so the browser doesn't block the popup.
  const handleSignIn = async () => {
    const email = state.email.trim();
    setSigningIn(true);
    setAuthError(null);
    try {
      await loginWithEmail(email);
    } catch (err: any) {
      console.error("CheckoutV2 sign-in failed:", err);
      // Surface the specific reason (e.g. the account-mismatch guard) when we
      // have one; otherwise a generic message.
      setAuthError(
        err?.message?.includes("email entered was")
          ? err.message
          : t("donationPage.errCouldNotSignIn")
      );
    } finally {
      setSigningIn(false);
    }
  };

  const handleChange = <K extends keyof DonationFormState>(
    key: K,
    value: DonationFormState[K]
  ) => {
    // Analytics: mirror the legacy checkout's per-field events.
    if (key === "frequency") {
      posthog?.capture("donation.type_selected", {
        donation_type: value === "monthly" ? "Monthly" : "One Time",
      });
    } else if (key === "amount") {
      // Preset amount selected (custom amount is handled via "otherAmount").
      const amount = Number(value);
      if (amount) {
        posthog?.capture("donation.amount_selected", {
          amount,
          is_custom_amount: false,
          donation_type: frequencyToDonationType(state.frequency),
        });
      }
    } else if (key === "otherAmount") {
      // Typed custom amount (skip the reset-to-empty a preset click triggers).
      const amount = Number(value);
      if (amount) {
        posthog?.capture("donation.amount_selected", {
          amount,
          is_custom_amount: true,
          donation_type: frequencyToDonationType(state.frequency),
        });
      }
    } else if (key === "paymentMethod") {
      posthog?.capture("donation.payment_method_selected", {
        payment_method: value === "checking" ? "ach" : (value as string),
      });
    } else if (key === "coverFees") {
      posthog?.capture("donation.cover_fees_toggled", {
        will_cover_fees: !!value,
      });
    } else if (key === "autoRenew") {
      posthog?.capture("donation.auto_renew_toggled", {
        will_auto_renew: !!value,
      });
    }
    setState((prev) => {
      // Switching frequency swaps the preset list; keep the selected amount if
      // it exists in the new list, otherwise fall back to that list's default.
      // Auto-renew is intentionally left untouched so a manual opt-out sticks.
      if (key === "frequency") {
        const frequency = value as DonationFormState["frequency"];
        const amounts = getAmountsForFrequency(frequency);
        const amount = amounts.includes(prev.amount)
          ? prev.amount
          : getDefaultAmount(frequency);
        return { ...prev, frequency, amount, otherAmount: "" };
      }
      return { ...prev, [key]: value };
    });
  };

  // A typed "Other Amount" overrides the preset selection.
  const effectiveAmount =
    state.otherAmount !== "" ? Number(state.otherAmount) : state.amount;
  const additionalAmount = state.coverFees ? calcCoverFee(effectiveAmount) : 0;

  // Below-minimum custom amount. Mirrors the legacy checkout's DonationAmountStep,
  // which errors when a TYPED amount is under $10 — the presets are all above it,
  // so an empty field or a preset selection is never flagged.
  const amountError =
    state.otherAmount !== "" && Number(state.otherAmount) < MIN_DONATION_AMOUNT
      ? t("donationPage.errMinimumDonation", { min: MIN_DONATION_AMOUNT })
      : null;

  // Auto-renew only applies to qualifying one-time donations.
  const autoRenewVisible = shouldShowAutoRenew(state.frequency, effectiveAmount);
  const autoRenewActive = autoRenewVisible && state.autoRenew;

  const handleSubmit = async () => {
    // Email: a signed-in member restores under their own account; a signed-out
    // visitor uses the email they entered.
    const email = (isAuthenticated ? user?.email : state.email)?.trim() || "";
    if (!email) {
      setSubmitError(
        isAuthenticated
          ? t("donationPage.errMissingAccountEmail")
          : t("donationPage.errEnterEmail")
      );
      return;
    }
    // A below-minimum custom amount blocks submit as well as showing inline —
    // otherwise the field reads as invalid while the donation still goes through.
    if (amountError) {
      setSubmitError(amountError);
      return;
    }
    // Per-method pre-validation.
    if (state.paymentMethod === "card" && (!stripe || !elements || !cardReady)) {
      setSubmitError(t("donationPage.errCardDetails"));
      return;
    }
    if (state.paymentMethod === "checking" && !plaid.plaidToken) {
      setSubmitError(t("donationPage.errConnectBank"));
      return;
    }
    // The trial stores the card for a later charge, so the ZIP is required for
    // the AVS check that a normal donation gets from the immediate charge.
    if (trialMode && !state.zip.trim()) {
      setSubmitError(t("donationPage.errZipRequired"));
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      // Signed-out safety guard (also covers clicking Donate before the blur
      // check ran): an existing account must sign in (Auth0 popup) rather than
      // (re-)pay as a new donor; post-login routing is handled by the page guard.
      // A signed-in member skips this — they're already authenticated.
      if (!isAuthenticated) {
        const { status, plan, amount } = await recognize(email);
        if (status === "sign-in") {
          await handleSignIn();
          return;
        }
        if (status === "complete-registration") {
          setRecognizedDonationType(donationTypeFromPlan(plan));
          setRecognizedAmount(amount);
          setAuthStep("complete-registration");
          return;
        }
      }

      // Invisible reCAPTCHA: no user interaction, resolves to a token we pass to
      // the checkout lambda. Runs after the recognition guard so an existing
      // account being routed to sign-in never burns a token.
      // executeAsync() can hang forever (bad/mismatched site key, challenge left
      // open), which would leave the button stuck on "Processing…" because the
      // finally below never runs. Race it so we always get an answer.
      const captchaToken = await Promise.race([
        recaptchaRef.current?.executeAsync(),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 15000)),
      ]);
      recaptchaRef.current?.reset();
      // Fail closed: no token (widget failed to mount, bad site key, challenge
      // dismissed) must not fall through to a charge. Real enforcement is still
      // server-side — the lambda has to verify the token.
      if (!captchaToken) {
        setSubmitError(t("donationPage.errCaptcha"));
        return;
      }

      const amountCents = toCents(effectiveAmount) + additionalCents(state, effectiveAmount);
      const donationType = frequencyToDonationType(state.frequency);

      // PayPal is a redirect flow: the backend builds a PayPal URL and we send the
      // donor there; the /paypal return page completes the donation and hands off
      // to the Thank You modal. Persist the new-donor context the redirect loses.
      if (state.paymentMethod === "paypal") {
        try {
          localStorage.setItem(
            "jg_paypal_pending",
            JSON.stringify({ email, isNewDonor: !isAuthenticated })
          );
          // The subscription is created after PayPal returns, on a page that
          // can't tell which donate page started this.
          localStorage.setItem(DONATE_PAGE_VERSION_KEY, donatePageVersion);
        } catch {}
        const { url } = await CheckoutV2Api.generatePaypal({
          customer: { first_name: "", last_name: "", email, auth0_id: undefined, meta_data: { language: "en" } },
          amount: amountCents,
          donationAmount: toCents(effectiveAmount),
          donationAdditionalAmount: additionalCents(state, effectiveAmount),
          monthly: state.frequency === "monthly",
          autoRenew: state.autoRenew,
          plan: getDonationPlanCode(amountCents, donationType),
          donationType,
          isGift: false,
          captchaToken,
          dev: process.env.REACT_APP_NODE_ENV === "dev",
        });
        if (!url) throw new Error(t("donationPage.errPaypalStart"));
        window.location.assign(url); // leaves the page → PayPal → /paypal return
        return;
      }

      // Card + ACH both charge via /checkout/v2 (no password → charge + plan sync,
      // no Auth0 login) and then go to the Thank You modal. A signed-in member's
      // existing Chargebee customer is matched by email, restoring their plan.
      let paymentMethodPayload: Record<string, unknown>;
      if (trialMode) {
        // A trial charges nothing today, so there's no PaymentIntent to confirm.
        // The card is tokenized and handed to Chargebee to store, which bills it
        // when the trial ends. createToken (rather than createPaymentMethod) is
        // deliberate: Chargebee's create_using_temp_token takes a `tok_`, and it
        // carries address_zip for the AVS check.
        const cardEl = elements!.getElement(CardNumberElement)!;
        const tokenResult = await stripe!.createToken(cardEl, {
          address_zip: state.zip.trim(),
        });
        if (tokenResult.error) throw new Error(tokenResult.error.message);
        paymentMethodPayload = { type: "card", token: tokenResult.token!.id };
      } else if (state.paymentMethod === "card") {
        const cardEl = elements!.getElement(CardNumberElement)!;
        const pm = await stripe!.createPaymentMethod({
          type: "card",
          card: cardEl,
          billing_details: { email },
        });
        if (pm.error) throw new Error(pm.error.message);
        const secret = await CheckoutV2Api.getDonationSecret(pm.paymentMethod!.id, amountCents);
        const paymentIntent = await confirmPaymentIntent(stripe!, secret);
        paymentMethodPayload = { type: "card", paymentIntent };
      } else {
        // Checking (ACH / direct_debit): send the Plaid-linked bank account.
        paymentMethodPayload = {
          type: "direct_debit",
          token: plaid.plaidToken!.token,
          accountId: plaid.plaidToken!.accountId,
          mask: plaid.plaidToken!.mask,
        };
      }

      const checkoutResult = await CheckoutV2Api.postCheckout({
        customer: { email, auth0_id: undefined, meta_data: { language: "en" } },
        paymentMethod: paymentMethodPayload,
        donationAmount: toCents(effectiveAmount),
        donationAdditionalAmount: additionalCents(state, effectiveAmount),
        amount: amountCents,
        monthly: state.frequency === "monthly",
        autoRenew: state.autoRenew,
        isGift: false,
        captchaToken,
        donatePageVersion,
        dev: process.env.REACT_APP_NODE_ENV === "dev",
        // Present only for a trial. The lambda keys the whole trial path off this
        // value and fixes the plan, price and 14-day period itself, so the
        // amounts above are ignored on that path.
        coupon: trialMode ? coupon.trim() : undefined,
        // No password → CheckoutV2 charges + syncs the plan, no Auth0 user.
      });

      // Analytics: mirror the legacy checkout's donation.completed event. The
      // gift is chosen later (Thank You), so is_gift/gift_choice are empty here.
      posthog?.capture("donation.completed", {
        amount: toCents(effectiveAmount),
        donation_type: donationType,
        payment_method: state.paymentMethod === "checking" ? "ach" : state.paymentMethod,
        will_cover_fees: additionalCents(state, effectiveAmount) > 0,
        will_auto_renew: state.autoRenew,
        is_gift: false,
        gift_choice: undefined,
        will_create_account: !isAuthenticated,
        new_plan_type: getDonationPlanCode(toCents(effectiveAmount), donationType),
      });

      // Donation done → suppress the active-redirect guard for this visit so the
      // applyPlanData flip to "active" below doesn't bounce the donor to
      // /checkout/one-time before the Thank You navigation runs.
      justDonatedRef.current = true;

      // Refresh a signed-in member's header immediately from the checkout
      // response (no re-query → avoids the Chargebee-list sync lag that would
      // otherwise briefly show their membership as still INACTIVE).
      const sub = checkoutResult?.subscription ?? checkoutResult?.body?.subscription;
      if (isAuthenticated && sub?.plan_id) {
        applyPlanData({
          plan: sub.plan_id,
          ttl: sub.current_term_end,
          subscriptionId: sub.id,
          subscriptionStatus: sub.status,
        } as any);
      }

      // Remember a new donor's amount/type so a later "complete registration"
      // (paid now, account created in a return visit) can still report them.
      if (!isAuthenticated) {
        rememberPaidDonation(email, amountCents, state.frequency);
      }

      // New donors create their login at the Thank You step (isNewDonor); a
      // signed-in member already has one, so they just see the confirmation/gift.
      // amount (cents) + frequency ride along for the account_created_post_donation
      // event fired when they create their account.
      history.push("/", {
        openThankYou: true,
        isNewDonor: !isAuthenticated,
        email,
        amount: amountCents,
        donationType: state.frequency,
        // The Thank You step attaches the gift + shipping address to this exact
        // subscription. Passing the id avoids Chargebee's eventually-consistent
        // email lookup, which can find nothing this soon after checkout.
        subscriptionId: sub?.id,
      });
    } catch (err: any) {
      console.error("CheckoutV2 submit failed:", err);
      // Prefer the API's own message (the free-trial ineligibility explanation, a
      // card decline reason...). The body arrives unwrapped through API Gateway's
      // proxy integration, but handle the enveloped and plain-text shapes too.
      const data = err?.response?.data;
      const apiMessage =
        typeof data === "string" ? data : data?.message ?? data?.body?.message;
      // Without one, never show err.message for an HTTP failure: Axios sets it to
      // "Request failed with status code 400", which tells the donor nothing.
      // Everything submitted here is payment, so point them at their card details.
      // Errors thrown before the request (Stripe's own card validation) have no
      // response and do carry a useful message.
      setSubmitError(
        (typeof apiMessage === "string" && apiMessage) ||
          (err?.response ? t("donationPage.errPaymentInvalid") : err?.message) ||
          t("donationPage.errSomethingWrong")
      );
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Box sx={{ backgroundColor: {md: SECTION_BG}, background:{xs: "linear-gradient(84.04deg, #412468 3.23%, #0C0B11 99.39%)", md: "none"}}}>
      {/* Invisible reCAPTCHA — kept outside the grid so it can't become a grid
          child and shift the columns. Renders only Google's fixed badge. */}
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={process.env.REACT_APP_RECAPTCHA_KEY as string}
      />
      <div style={{maxWidth: 1280, margin: "0 auto"}}>
        <Box
          sx={{
            display: {xs:"block" ,md:"grid"},
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.5fr) minmax(0, 1fr)" },
            // gap: { xs: 3, md: 4 },
            alignItems: "start",
          }}
        >
          {/* Left: form card */}
          <Box
            sx={{
              backgroundColor: FORM_BG,
              // borderRadius: "16px",
              p: {xs: 3, md: 4},
              maxWidth:{xs: "100%", md: 756},
            }}
          >
            <DonationForm
              state={state}
              onChange={handleChange}
              onSubmit={handleSubmit}
              effectiveAmount={effectiveAmount}
              showAutoRenew={autoRenewVisible}
              variant={variant}
              onSignIn={handleSignIn}
              onEmailBlur={handleEmailBlur}
              checkingEmail={checkingEmail}
              onUseDifferentEmail={handleUseDifferentEmail}
              onCompleteRegistration={handleCompleteRegistration}
              signingIn={signingIn}
              authError={authError}
              onCardChange={(c) => setCardComplete((s) => ({ ...s, number: c }))}
              onExpiryChange={(c) => setCardComplete((s) => ({ ...s, expiry: c }))}
              onCvcChange={(c) => setCardComplete((s) => ({ ...s, cvc: c }))}
              achReady={plaid.ready}
              achConnectedMask={plaid.plaidToken?.mask ?? null}
              onConnectBank={() => plaid.open()}
              submitting={submitting}
              submitError={submitError}
              donorName={user?.given_name || user?.name}
              membershipInactive={membershipInactive}
              membershipEndDate={membershipEndDate}
              infoBelowAmount={infoBelowAmount}
              amountError={amountError}
              trialMode={trialMode}
            />
          </Box>

          {/* Right: gift always; summary desktop-only (mobile summary lives in DonationForm) */}
          {/* The trial summary is a single line, so the wide gap sized for the
              full donation summary would leave a hole above the gift photo. */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 3, md: trialMode ? 4 : 12.3 },maxWidth: {xs: "100%", md: 446}, margin: {xs:0, md: 4}, padding:{xs: 4, md: 0} }}>
            <Box sx={{ display: { xs: "none", md: "block" } }}>
            <DonationSummaryV2
              frequency={state.frequency}
              amount={effectiveAmount}
              additionalAmount={additionalAmount}
              autoRenew={autoRenewActive}
              trialMode={trialMode}
            /></Box>
            <div>
            <MemberGiftSlider />
            <Typography
              sx={{
                fontFamily: FONT_SANS,
                fontSize: {xs: "14px", md: "16px"},
                color: "rgba(255,255,255)",
                textAlign: "center",
                lineHeight: 1.2,
                mt: 2,
              }}
            >
              {/* A trial has no donation minimum to meet — gifts come later, when
                  the trial turns into a paid membership. */}
              {trialMode
                ? t("donationPage.trialGiftNote", { days: TRIAL_DAYS })
                : t("donationPage.giftMinimumNote", { amount: GIFT_MINIMUM })}
            </Typography>
            </div>
          </Box>
        </Box>
      </div>
    </Box>
  );
};

/**
 * The section calls useStripe()/useElements(), so it provides the Elements context
 * itself rather than relying on each page to remember the wrapper — CheckoutV3
 * didn't, and the page crashed on mount with "Could not find Elements context".
 */
const DonateSection = (props: DonateSectionProps) => (
  <Elements stripe={stripePromise}>
    <DonateSectionInner {...props} />
  </Elements>
);

export default DonateSection;
